// The model drawer (acceptance I1–I6, J, M, N; Versions and Settings live in
// their own files). Opens from Details / ⋯; dialogs it opens sit above it.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { modelServingApi } from "../../api/client";
import { COLORS } from "../../theme/colors";
import {
  drawerActions, eventLine, eventTime, logsText, modelStatus, overviewMetrics, usedByItems, worksWith,
} from "./modelServingModel";
import ModelDrawerSettings from "./ModelDrawerSettings";
import ModelDrawerVersions from "./ModelDrawerVersions";
import { Badge, Btn, LAYER, MONO, Notice, SectionHead, errorText, onTop } from "./modelServingUi";

const TABS = [["overview", "Overview"], ["versions", "Versions"], ["activity", "Activity"],
  ["endpoint", "Endpoint"], ["settings", "Settings"]];

/** Load ``fetcher()`` once per ``deps``; {data, err}. A refresh keeps showing the
 * previous data until the new data arrives (no "Loading…" flash every poll). */
function useLoad(fetcher, deps) {
  const [state, setState] = useState({ data: null, err: null });
  useEffect(() => {
    let live = true;
    fetcher()
      .then((r) => { if (live) setState({ data: r.data, err: null }); })
      .catch((e) => { if (live) setState({ data: null, err: errorText(e) }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

function Loading({ state, children }) {
  if (state.err) return <Notice tone="bad" icon="!">{state.err}</Notice>;
  if (!state.data) return <div style={{ fontSize: 12.5, color: COLORS.textDim }}>Loading…</div>;
  return children(state.data);
}

function Overview({ model, stamp }) {
  const state = useLoad(() => modelServingApi.metrics(model.key), [model.key, stamp]);
  return (
    <Loading state={state}>
      {(m) => (
        <>
          <div data-lc="health-metrics" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
            {overviewMetrics(m).map((t) => (
              <div key={t.key} data-lc={t.lc} data-testid={`metric-${t.key}`}
                style={{ border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 12 }}>
                <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, color: COLORS.textDim }}>{t.label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, margin: "4px 0", fontVariantNumeric: "tabular-nums" }}>{t.value}</div>
                <div style={{ fontSize: 11.5, color: COLORS.textMuted }}>{t.sub}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: COLORS.textDim, marginTop: 12 }}>
            Costs are approximate: AWS on-demand price × time the GPU was running.
          </p>
        </>
      )}
    </Loading>
  );
}

function Activity({ model, stamp }) {
  const state = useLoad(() => modelServingApi.events(model.key), [model.key, stamp]);
  const [logs, setLogs] = useState(null);
  const openLogs = async (e) => {
    if (!e.currentTarget.open || logs) return;
    try {
      setLogs(logsText((await modelServingApi.logs(model.key)).data));
    } catch (err) {
      setLogs(`Couldn't read the logs: ${errorText(err)}`);
    }
  };
  return (
    <>
      <Loading state={state}>
        {(events) => (
          <div data-lc="activity-log" style={{ display: "grid", gap: 2 }}>
            {events.length === 0 && <div style={{ fontSize: 12.5, color: COLORS.textDim }}>Nothing has happened yet.</div>}
            {events.map((e) => {
              const line = eventLine(e);
              return (
                <div key={line.id} data-testid="activity-event" data-error={line.error ? "1" : undefined}
                  style={{ display: "grid", gridTemplateColumns: "56px 1fr", gap: 10, padding: "8px 10px", borderRadius: 10,
                    background: line.error ? `${COLORS.pink}12` : "transparent" }}>
                  <div style={{ fontSize: 11.5, color: COLORS.textDim, fontVariantNumeric: "tabular-nums" }}>{eventTime(line.when)}</div>
                  <div>
                    <div style={{ fontSize: 13, color: line.error ? COLORS.pink : COLORS.text }}>{line.what}</div>
                    <div style={{ fontSize: 11.5, color: COLORS.textDim }}>by <b>{line.by}</b></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Loading>
      <details data-lc="logs" style={{ marginTop: 12 }} onToggle={openLogs}>
        <summary style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: COLORS.accentText }}>
          View server logs &amp; events
        </summary>
        <pre data-testid="model-logs"
          style={{ marginTop: 8, padding: 12, borderRadius: 10, background: COLORS.bg, border: `1px solid ${COLORS.border}`,
            fontFamily: MONO, fontSize: 11.5, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 320, overflow: "auto" }}>
          {logs || "Loading…"}
        </pre>
      </details>
    </>
  );
}

function Endpoint({ model, stamp }) {
  const navigate = useNavigate();
  const state = useLoad(() => modelServingApi.usage(model.key), [model.key, stamp]);
  const kv = [
    ["Model name for agents", <span key="n" style={{ fontFamily: MONO }}>{model.served_model_name || model.key}</span>],
    ["Endpoint", model.endpoint ? "Registered · private network, mTLS" : "Registered on the first warm-up"],
    ["Works with", worksWith(model)],
  ];
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(140px,auto) 1fr", gap: "8px 14px", fontSize: 13 }}>
        {kv.map(([k, v]) => [
          <div key={`${k}-k`} style={{ color: COLORS.textDim }}>{k}</div>, <div key={`${k}-v`}>{v}</div>,
        ])}
      </div>
      <Loading state={state}>
        {(usage) => {
          const items = usedByItems(usage);
          return (
            <>
              <SectionHead title="Used by" count={items.length} />
              <div data-lc="used-by" style={{ display: "grid", gap: 8 }}>
                {items.length === 0 && <div style={{ fontSize: 12.5, color: COLORS.textDim }}>No ranking or agent uses this model yet.</div>}
                {items.map((it) => (
                  <div key={`${it.kind}-${it.id || it.label}`}
                    style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 12px", borderRadius: 12,
                      border: `1px solid ${COLORS.border}` }}>
                    <span aria-hidden="true">{it.kind === "ranking" ? "🏆" : "◎"}</span>
                    <div style={{ flex: 1, fontSize: 13 }}>{it.label}</div>
                    {it.kind === "ranking"
                      ? (
                        <Btn sm onClick={() => window.dispatchEvent(new CustomEvent("admin:goto-tab", { detail: { id: "rankings" } }))}>
                          Open rankings
                        </Btn>
                      )
                      : <Btn sm onClick={() => navigate(`/library/${it.id}`)}>Open agent</Btn>}
                  </div>
                ))}
              </div>
            </>
          );
        }}
      </Loading>
      <p style={{ fontSize: 12, color: COLORS.textDim, marginTop: 12 }}>A model can&apos;t be removed while anything here uses it.</p>
    </>
  );
}

export default function ModelDrawer({ model, preset, initialTab = "overview", onClose, onDialog, onAct, stamp }) {
  const [tab, setTab] = useState(initialTab);
  const [err, setErr] = useState(null);
  // Runs one drawer action through the page (which reloads); its error shows here,
  // not behind the drawer.
  const act = async (fn) => {
    setErr(null);
    try {
      await onAct(fn);
    } catch (e) {
      setErr(errorText(e));
    }
  };
  const status = modelStatus(model);
  const acts = drawerActions(status, model);
  const serving = model.serving || {};
  return onTop(
    <>
      <div data-testid="drawer-scrim" onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(5,7,16,.35)", zIndex: LAYER.scrim }} />
      <aside data-testid="model-drawer" role="dialog" aria-label="Model details"
        style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(600px, 100vw)", zIndex: LAYER.drawer, overflowY: "auto",
          background: COLORS.surface, borderLeft: `1px solid ${COLORS.border}`, boxShadow: "-12px 0 40px rgba(0,0,0,.25)",
          color: COLORS.text }}>
        <button type="button" aria-label="Close" onClick={onClose}
          style={{ position: "absolute", top: 14, right: 14, border: "none", background: "transparent", color: COLORS.textDim,
            fontSize: 16, cursor: "pointer" }}>✕</button>
        <div style={{ padding: "20px 22px 10px", paddingRight: 48 }}>
          <h3 data-testid="drawer-name" style={{ margin: "0 0 4px", fontSize: 18 }}>{model.display_name}</h3>
          <div style={{ fontFamily: MONO, fontSize: 12, color: COLORS.textDim, wordBreak: "break-all" }}>{model.hf_repo || model.key}</div>
        </div>
        <div style={{ display: "flex", gap: 8, padding: "0 22px 12px", alignItems: "center", flexWrap: "wrap" }}>
          <Badge status={status} />
          <div style={{ flex: 1 }} />
          {acts.warm && (
            <Btn sm kind="primary" data-lc="warm-up-drawer" disabled={acts.warm.disabled}
              onClick={() => onDialog("warm")}>{acts.warm.label}</Btn>
          )}
          {acts.stop && <Btn sm kind="danger" onClick={() => onDialog("stop")}>⏹ Stop</Btn>}
        </div>
        <div role="tablist" style={{ display: "flex", gap: 4, padding: "0 22px", borderBottom: `1px solid ${COLORS.border}`, overflowX: "auto" }}>
          {TABS.map(([id, text]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} data-lc={`tab-${id}`} onClick={() => setTab(id)}
              style={{ padding: "9px 12px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13,
                fontWeight: 600, color: tab === id ? COLORS.text : COLORS.textDim,
                borderBottom: `2px solid ${tab === id ? COLORS.accent : "transparent"}` }}>{text}</button>
          ))}
        </div>
        <div style={{ padding: "18px 22px 28px" }}>
          {err && <Notice testid="drawer-error" tone="bad" icon="!">{err}</Notice>}
          {tab === "overview" && <Overview model={model} stamp={stamp} />}
          {tab === "versions" && <ModelDrawerVersions model={model} serving={serving} onDialog={onDialog} onAct={act} />}
          {tab === "activity" && <Activity model={model} stamp={stamp} />}
          {tab === "endpoint" && <Endpoint model={model} stamp={stamp} />}
          {tab === "settings" && <ModelDrawerSettings key={model.key} model={model} preset={preset} serving={serving} onChanged={() => act(null)} />}
          <div style={{ marginTop: 22, borderTop: `1px solid ${COLORS.border}`, paddingTop: 16 }}>
            <Btn sm kind="danger" data-lc="remove" onClick={() => onDialog("remove")}>Remove model…</Btn>
          </div>
        </div>
      </aside>
    </>,
  );
}
